import React from 'react';

import styled from 'styled-components';

import Skeleton from '@components/skeletons/Skeleton';

import { SearchInfoDetails, VariationCard } from './styled';

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkeletonDetails = styled(SearchInfoDetails)`
  margin-top: 16px;
`;

const SkeletonLine = styled.div<{ $width?: string }>`
  width: ${({ $width }) => $width || '100%'};
`;

const SkeletonControl = styled.div`
  width: 100%;
  height: 60px;
`;

const SkeletonUtilityRow = styled.div`
  display: flex;
  justify-content: flex-end;
  min-height: 42px;
`;

const SkeletonContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SkeletonVariationCard = styled(VariationCard)`
  min-height: 170px;
`;

const SkeletonVariationColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkeletonVariationPrice = styled(SkeletonVariationColumn)`
  align-items: flex-end;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    align-items: flex-start;
  }
`;

const SkeletonAction = styled.div`
  grid-column: 2;
  justify-self: end;
  width: 180px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    grid-column: 1;
    justify-self: stretch;
    width: 100%;
  }
`;

export const PackageSearchInfoSkeleton = () => (
  <div data-testid='package-search-info-skeleton' aria-hidden='true'>
    <SkeletonStack>
      <SkeletonControl>
        <Skeleton height={60} borderRadius={8} />
      </SkeletonControl>
      <SkeletonControl>
        <Skeleton height={60} borderRadius={8} />
      </SkeletonControl>
    </SkeletonStack>
    <SkeletonDetails>
      <SkeletonStack>
        <SkeletonLine $width='78%'>
          <Skeleton height={18} borderRadius={4} />
        </SkeletonLine>
        <SkeletonLine $width='70%'>
          <Skeleton height={18} borderRadius={4} />
        </SkeletonLine>
        <SkeletonLine $width='84%'>
          <Skeleton height={18} borderRadius={4} />
        </SkeletonLine>
        <SkeletonLine $width='64%'>
          <Skeleton height={18} borderRadius={4} />
        </SkeletonLine>
      </SkeletonStack>
      <SkeletonStack>
        <SkeletonLine $width='52%'>
          <Skeleton height={20} borderRadius={4} />
        </SkeletonLine>
        <Skeleton height={18} borderRadius={4} />
        <Skeleton height={18} borderRadius={4} />
        <Skeleton height={18} borderRadius={4} />
      </SkeletonStack>
    </SkeletonDetails>
  </div>
);

export const PackageVariationSkeleton = () => (
  <SkeletonVariationCard
    data-testid='package-variation-skeleton'
    aria-hidden='true'
  >
    <SkeletonVariationColumn>
      <Skeleton width={180} height={20} borderRadius={4} />
      <Skeleton width={140} height={16} borderRadius={4} />
    </SkeletonVariationColumn>
    <SkeletonVariationPrice>
      <Skeleton width={90} height={20} borderRadius={4} />
      <Skeleton width={115} height={16} borderRadius={4} />
      <Skeleton width={100} height={30} borderRadius={4} />
    </SkeletonVariationPrice>
    <SkeletonAction>
      <Skeleton height={42} borderRadius={8} />
    </SkeletonAction>
  </SkeletonVariationCard>
);

const PackageTemplateSkeleton = () => (
  <SkeletonContent data-testid='package-template-skeleton' aria-hidden='true'>
    <SkeletonUtilityRow data-testid='package-utility-skeleton'>
      <Skeleton width={180} height={42} borderRadius={8} />
    </SkeletonUtilityRow>
    <PackageVariationSkeleton />
    <PackageVariationSkeleton />
  </SkeletonContent>
);

export default PackageTemplateSkeleton;
